import { useState, useEffect, useRef } from "react";
import BaseModal from "../../../components/BaseModal";
import AppToast from "../../../components/AppToast";
import "../../../assets/css/payslip-upload.css";

const API_BASE = process.env.REACT_APP_API_BASE;

export default function PayslipUpload() {
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // ✅ File input ref (IMPORTANT)
  const fileInputRef = useRef(null);

  /* ================= PREVENT REFRESH ================= */
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = "";
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () =>
      window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [loading]);

  /* ================= RESET FORM ================= */
  const resetForm = () => {
    setFile(null);
    setMonth("");
    setProgress(0);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  /* ================= START CONFIRM ================= */
  const handleUploadClick = () => {
    if (!file || !month) {
      setToast({
        type: "error",
        title: "Missing Information",
        message: "Please select month and upload a file.",
      });
      return;
    }

    setConfirmOpen(true);
  };

  /* ================= CONFIRM UPLOAD ================= */
  const handleConfirmUpload = () => {
    setConfirmOpen(false);

    if (!API_BASE) {
      setToast({
        type: "error",
        title: "Configuration Error",
        message: "API base URL is not configured.",
      });
      return;
    }

    setLoading(true);
    setProgress(0);

    const formData = new FormData();
    formData.append("file", file);
    formData.append("month", month);

    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API_BASE}/loans/upload_payslip.php`, true);
    xhr.withCredentials = true;

    /* ================= PROGRESS ================= */
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setProgress(percent);
      }
    };

   /* ================= SUCCESS ================= */
xhr.onload = async () => {
  setLoading(false);

  if (xhr.status !== 200) {
    let message = `HTTP Error ${xhr.status}`;

    try {
      const errorData = JSON.parse(xhr.responseText);
      message = errorData.message || message;
    } catch (e) {
      message = xhr.responseText || message;
    }

    setToast({
      type: "error",
      title: "Server Error",
      message,
    });

    return;
  }

  try {
    const data = JSON.parse(xhr.responseText);

    if (!data.success) {
      setToast({
        type: "error",
        title: "Upload Failed",
        message: data.message || "Something went wrong.",
      });
      return;
    }

    /*
      START CHUNK PROCESSING
    */
    setLoading(true);
    setProgress(5);

    let startRow = data.start_row || 2;
    const chunkSize = data.chunk_size || 500;

    let totalInserted = 0;
    let loopCount = 0;

    while (true) {
      loopCount++;

      const res = await fetch(
        `${API_BASE}/loans/process_payslip_chunk.php`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            batch_id: data.batch_id,
            start_row: startRow,
            chunk_size: chunkSize,
          }),
        }
      );

      const chunkData = await res.json();

      if (!res.ok || !chunkData.success) {
        throw new Error(
          chunkData.message || "Payslip processing failed."
        );
      }

      totalInserted += chunkData.inserted || 0;

      startRow = chunkData.next_row;

      /*
        UPDATE PROGRESS
      */
     setProgress(Math.min(98, 5 + loopCount * 2));

      /*
        FINISHED
      */
     if (
  chunkData.done ||
  chunkData.inserted === 0 ||
  chunkData.seen_rows === 0 ||
  startRow > 70000
) {
  break;
}
    }

    setProgress(100);
    setLoading(false);

    setToast({
      type: "success",
      title: "Upload Successful",
      message: `Payslip for ${month} uploaded successfully. ${totalInserted} records inserted.`,
    });

    resetForm();

  } catch (err) {
    setLoading(false);

    setToast({
      type: "error",
      title: "Processing Failed",
      message:
        err.message ||
        "Something went wrong while processing payslip.",
    });
  }
};

    /* ================= NETWORK ERROR ================= */
    xhr.onerror = () => {
      setLoading(false);
      setToast({
        type: "error",
        title: "Network Error",
        message:
          "Unable to reach server. Please check your connection or API configuration.",
      });
    };

    xhr.send(formData);
  };

  return (
    <>
      {/* ================= FULL SCREEN OVERLAY ================= */}
      {loading && (
        <div className="fullscreen-overlay">
          <div className="overlay-content">
            <div className="spinner-border text-primary mb-3" />
            <h5>Uploading Payslip...</h5>

            <div className="progress mt-4 w-100">
              <div
                className="progress-bar progress-bar-striped progress-bar-animated"
                style={{ width: `${progress}%` }}
              >
                {progress}%
              </div>
            </div>

            <small className="text-muted mt-3">
              Please wait. Do not refresh or close this page.
            </small>
          </div>
        </div>
      )}

      {/* ================= PAGE ================= */}
      <div className="container-fluid payslip-wrapper">
        <div className="card payslip-card">
          <div className="payslip-header">
            <h4 className="payslip-title">Upload Monthly Payslip</h4>
            <p className="payslip-subtitle">
              Select month and upload staff payslip file.
            </p>
          </div>

          {/* Month */}
          <div className="mb-4">
            <label className="form-label payslip-label">
              Select Payslip Month
            </label>
            <input
              type="month"
              className="form-control payslip-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              disabled={loading}
            />
          </div>

          {/* File */}
          <div className="mb-4">
            <label className="form-label payslip-label">
              Upload Payslip File
            </label>

            <input
              type="file"
              ref={fileInputRef} // ✅ important
              className="form-control"
              accept=".xlsx,.xls,.csv"
              disabled={loading}
              onChange={(e) => {
                const selectedFile = e.target.files[0];
                if (!selectedFile) return;

                const allowedTypes = [
                  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  "application/vnd.ms-excel",
                  "text/csv",
                ];

                if (!allowedTypes.includes(selectedFile.type)) {
                  setToast({
                    type: "error",
                    title: "Invalid File Type",
                    message:
                      "Only Excel files (.xlsx, .xls, .csv) are allowed.",
                  });
                  e.target.value = "";
                  return;
                }

                setFile(selectedFile);
              }}
            />
          </div>

          {/* Action */}
          <div className="text-end">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUploadClick}
              disabled={loading}
            >
              <i className="fa fa-cloud-upload-alt me-2"></i>
              Upload Payslip
            </button>
          </div>
        </div>
      </div>

      {/* ================= CONFIRM MODAL ================= */}
      <BaseModal
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        title="Confirm Upload"
        size="md"
      >
        <p>
          Are you sure you want to upload payslip for{" "}
          <strong>{month}</strong>?
        </p>

       <div className="confirm-actions">
  <button
    className="confirm-btn cancel-btn"
    onClick={() => setConfirmOpen(false)}
  >
    Cancel
  </button>

  <button
    className="confirm-btn primary-btn"
    onClick={handleConfirmUpload}
  >
    Confirm Upload
  </button>
</div>
      </BaseModal>

      <AppToast toast={toast} onClose={() => setToast(null)} />
    </>
  );
}